import React, { useState, useEffect, createContext } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import GalleryView from './components/GalleryView';
import ItemDetailPage from './components/ItemDetailPage';
import LoginScreen from './components/LoginScreen';
import ScrollToTop from './components/ScrollToTop';
import Header from './components/Header';
import Footer from './components/Footer';
import './index.css';
import useFashionItems from './data/useFashionItems';
import RequestModal from './components/RequestModal';
import { AuthProvider, useAuth } from './AuthContext';
import { updateUserLastSeen } from './data/acceptedUsers';

const EMAIL_KEY = 'fashion_gallery_user';
export const RequestModalContext = createContext();

// Get user's IP address
const getUserIP = async () => {
  try {
    const response = await fetch('https://api.ipify.org?format=json');
    const data = await response.json();
    return data.ip;
  } catch (error) {
    console.error('Error getting IP:', error);
    return null;
  }
};

// Check if user is internal traffic
const isInternalTraffic = (userIP) => {
  return userIP === '174.164.8.102';
};

function usePageView() {
  const location = useLocation();
  const [userIP, setUserIP] = useState(null);
  const [isInternal, setIsInternal] = useState(false);

  // Get user's IP on component mount
  useEffect(() => {
    getUserIP().then(ip => {
      setUserIP(ip);
      setIsInternal(isInternalTraffic(ip));
    });
  }, []);

  useEffect(() => {
    if (typeof window.gtag === "function") {
      const eventParams = {
        page_path: location.pathname + location.search,
        page_location: window.location.href,
        page_title: document.title,
      };

      // Add traffic_type parameter for internal traffic filtering
      if (isInternal) {
        eventParams.traffic_type = 'internal';
      }

      window.gtag("event", "page_view", eventParams);
    }
  }, [location, isInternal]);
}

// This is a wrapper so you can use hooks inside Router
function PageViewHandler() {
  usePageView();
  return null;
}

function AppContent() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const { items, loading } = useFashionItems();
  const [showRequestModal, setShowRequestModal] = useState(false);

  useEffect(() => {
    if (user?.email) {
      // Update lastSeen immediately on load
      updateUserLastSeen(user.email);

      // And then update periodically every 60 seconds
      const intervalId = setInterval(() => {
        updateUserLastSeen(user.email);
      }, 60000); // 60 seconds

      return () => clearInterval(intervalId);
    }
  }, [user]);

  const handleLogout = () => {
    logout();
  };

  const handleLogin = (user) => {
    // setUser(user); // This is now handled by AuthContext
  };

  const showHeader = location.pathname !== '/login';

  if (!user) {
    return <LoginScreen onLogin={handleLogin} />;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black mx-auto mb-4"></div>
          <p className="text-gray-600">Loading Titled Works Curation...</p>
        </div>
      </div>
    );
  }

  return (
    <RequestModalContext.Provider value={{ setShowRequestModal }}>
      <PageViewHandler />
      <Header user={user} />
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<GalleryView items={items} user={user} />} />
        <Route path="/item/:id" element={<ItemDetailPage user={user} />} />
      </Routes>
      <Footer user={user} />
      
      {/* Request Modal */}
      <RequestModal
        isOpen={showRequestModal}
        onClose={() => setShowRequestModal(false)}
        user={user}
      />
    </RequestModalContext.Provider>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <AppContent />
      </Router>
    </AuthProvider>
  );
}

export default App; 